/**
 * Pure, DOM-free helpers behind VisualiserTextBox's auto-fit. The DOM wrapper in
 * VisualiserTextBox.vm.ts supplies the measurement; everything here is unit-testable.
 */

/**
 * The per-font default padding lives in `shared/view/uv-inserts/text-box-padding.ts` so the canvas
 * cart preview (`renderCardPreview`) can resolve the same em values to px at the drawn size —
 * `shared/` cannot import from the theme, so the map moved rather than the canvas re-declaring it.
 * Re-exported here to keep this module's public surface.
 */
export { getDefaultTextBoxPadding } from "@shared/view/uv-inserts/text-box-padding"

export type EffectiveMaxHeightOptions = {
  /** CSS length string from config `size.maxHeight` (only the leading number is used). */
  maxHeight?: string
  maxLines?: number
  lineHeight: number
  fontSize: number
  /** paddingTop + paddingBottom in px. `scrollHeight` includes padding, so the line cap must too. */
  verticalPadding?: number
}

/**
 * The height (px) the rendered text may occupy: the smaller of the configured `maxHeight` and
 * `maxLines` worth of line boxes plus vertical padding. `undefined` when neither limit is set.
 */
export function computeEffectiveMaxHeight(options: EffectiveMaxHeightOptions): number | undefined {
  const { maxHeight, maxLines, lineHeight, fontSize, verticalPadding = 0 } = options
  const maxHeightValue = maxHeight ? parseFloat(maxHeight) : undefined
  const maxHeightPx = maxHeightValue !== undefined && !Number.isNaN(maxHeightValue) ? maxHeightValue : undefined
  if (!maxLines) {
    return maxHeightPx
  }
  const maxLinesHeight = maxLines * lineHeight * fontSize + verticalPadding
  return maxHeightPx !== undefined ? Math.min(maxHeightPx, maxLinesHeight) : maxLinesHeight
}

export type FitFontSizeOptions = {
  /** Configured (maximum) size. */
  fontSize: number
  effectiveMaxHeight?: number
  minFontSize?: number
  step?: number
  /** Returns the content height (e.g. `scrollHeight`) the element would have at this font size. */
  measure: (fontSize: number) => number
}

/**
 * Largest font size, in `step` decrements from `fontSize` down to `minFontSize`, whose measured
 * height fits `effectiveMaxHeight`. With no limit, or when the configured size already fits, the
 * configured size is returned (so text that previously forced a shrink grows back once it is removed).
 *
 * `scrollHeight` is an integer, so the limit is rounded up before comparing: 96.6px of lines in a
 * 96px box measures 97 and must still count as fitting. The floor is a floor, not a minimum: a font
 * configured below `minFontSize` never grows to it, whatever the text. Text set below the default
 * 8px that must still shrink (the 6.33px Story of Us caption) sets its own `minFontSize` (ALBAU-950).
 */
export function fitFontSize(options: FitFontSizeOptions): number {
  const { fontSize, effectiveMaxHeight, minFontSize = 8, step = 0.5, measure } = options

  if (!effectiveMaxHeight) return fontSize
  const ceiling = Math.ceil(effectiveMaxHeight)
  if (measure(fontSize) <= ceiling) return fontSize

  let testFontSize = fontSize
  while (testFontSize > minFontSize) {
    testFontSize -= step
    if (measure(testFontSize) <= ceiling) {
      return testFontSize
    }
  }
  return Math.min(fontSize, minFontSize)
}
